/**
 * Admin API client — prefers httpOnly cookie session (`bh_at` / `bh_rt`).
 * Cookie-session mutations require `X-CSRF-Token` (native Bearer remains CSRF-exempt by design).
 * Memory-only Bearer is transitional only; cookie-first is the default after login.
 */
const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3333';
const USER_KEY = 'admin_user_data';
const CSRF_HEADER = 'X-CSRF-Token';
const MUTATING = new Set(['POST', 'PUT', 'PATCH', 'DELETE']);

interface ApiResponse<T> {
  success: boolean;
  data: T;
  message?: string;
  error?: string;
}

class ApiClient {
  private baseUrl: string;
  /** In-memory only — never written to localStorage. Prefer cookies over this. */
  private memoryToken: string | null = null;
  /** Plaintext CSRF from `X-CSRF-Token` response header (not document.cookie). */
  private csrfToken: string | null = null;
  private refreshing: Promise<boolean> | null = null;
  private csrfBootstrap: Promise<void> | null = null;

  constructor(baseUrl: string) {
    this.baseUrl = baseUrl;
    // Migrate away from legacy localStorage JWT (one-time clear).
    try {
      if (localStorage.getItem('admin_token')) {
        localStorage.removeItem('admin_token');
      }
    } catch {
      /* ignore */
    }
  }

  setToken(token: string | null) {
    this.memoryToken = token;
  }

  getToken(): string | null {
    return this.memoryToken;
  }

  /** Prefer httpOnly cookies; clear any in-memory Bearer so CSRF applies to cookie sessions. */
  preferCookieSession() {
    this.memoryToken = null;
  }

  /** Clear CSRF + memory token on logout / failed admin checks. */
  clearSessionSecrets() {
    this.memoryToken = null;
    this.csrfToken = null;
    this.refreshing = null;
    this.csrfBootstrap = null;
  }

  private captureCsrf(response: Response) {
    const next = response.headers.get(CSRF_HEADER) || response.headers.get('x-csrf-token');
    if (next) {
      this.csrfToken = next;
    }
  }

  private async ensureCsrf(): Promise<void> {
    if (this.csrfToken) return;
    if (this.csrfBootstrap) return this.csrfBootstrap;

    this.csrfBootstrap = (async () => {
      try {
        const res = await fetch(`${this.baseUrl}/api/auth/csrf`, {
          method: 'GET',
          credentials: 'include',
        });
        this.captureCsrf(res);
      } catch {
        /* login bootstrap can still issue a token */
      } finally {
        this.csrfBootstrap = null;
      }
    })();

    return this.csrfBootstrap;
  }

  private async tryRefresh(): Promise<boolean> {
    if (this.refreshing) return this.refreshing;
    this.refreshing = (async () => {
      try {
        await this.ensureCsrf();
        const headers: Record<string, string> = {
          'Content-Type': 'application/json',
        };
        if (this.csrfToken) {
          headers[CSRF_HEADER] = this.csrfToken;
        }
        // Cookie refresh must not send memory Bearer (would CSRF-exempt the call).
        const res = await fetch(`${this.baseUrl}/api/auth/sso/refresh`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: '{}',
        });
        this.captureCsrf(res);
        if (!res.ok) return false;
        // Keep cookie-first: ignore any accessToken body for memory Authorization.
        this.memoryToken = null;
        return true;
      } catch {
        return false;
      } finally {
        this.refreshing = null;
      }
    })();
    return this.refreshing;
  }

  private async request<T>(
    endpoint: string,
    options: RequestInit = {},
    allowRetry = true
  ): Promise<ApiResponse<T>> {
    const method = (options.method || 'GET').toUpperCase();
    if (MUTATING.has(method)) {
      await this.ensureCsrf();
    }

    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      'Content-Type': 'application/json',
      ...(options.headers as Record<string, string>),
    };

    // Only send Authorization when an explicit memory token remains (legacy/transitional).
    // Cookie sessions rely on credentials + cookie bridge so CSRF stays enforceable.
    if (this.memoryToken) {
      headers['Authorization'] = `Bearer ${this.memoryToken}`;
    } else if (MUTATING.has(method) && this.csrfToken) {
      headers[CSRF_HEADER] = this.csrfToken;
    }

    try {
      const response = await fetch(url, {
        ...options,
        headers,
        credentials: 'include',
      });
      this.captureCsrf(response);

      if (response.status === 401 && allowRetry) {
        const refreshed = await this.tryRefresh();
        if (refreshed) {
          return this.request<T>(endpoint, options, false);
        }
        this.clearSessionSecrets();
        try {
          sessionStorage.removeItem(USER_KEY);
          localStorage.removeItem(USER_KEY);
        } catch {
          /* ignore */
        }
        if (window.location.pathname !== '/admin/login') {
          window.location.href = '/admin/login';
        }
        throw new Error('Unauthorized. Please login again.');
      }

      const rawBody = await response.text();
      let data: unknown = null;
      if (rawBody.trim()) {
        try {
          data = JSON.parse(rawBody) as unknown;
        } catch {
          throw new Error(`Invalid response: ${rawBody.slice(0, 500) || response.statusText}`);
        }
      }

      const payload = (typeof data === 'object' && data !== null ? data : {}) as Record<string, unknown>;

      if (!response.ok) {
        console.error('API Error:', {
          status: response.status,
          statusText: response.statusText,
          data,
          url,
        });
        const errorMessage =
          (typeof payload.message === 'string' && payload.message) ||
          (typeof payload.error === 'string' && payload.error) ||
          (typeof payload.details === 'string' && payload.details) ||
          `Request failed with status ${response.status}`;
        throw new Error(errorMessage);
      }

      if (payload.success !== undefined) {
        return payload as unknown as ApiResponse<T>;
      }

      return {
        success: true,
        data: data as T,
      };
    } catch (error) {
      console.error('API Request Error:', {
        endpoint,
        error: error instanceof Error ? error.message : String(error),
      });
      throw error instanceof Error ? error : new Error('Network error');
    }
  }

  async get<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  async post<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: JSON.stringify(body ?? {}),
    });
  }

  async put<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: JSON.stringify(body ?? {}),
    });
  }

  async patch<T>(endpoint: string, body?: unknown): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: JSON.stringify(body ?? {}),
    });
  }

  async delete<T>(endpoint: string): Promise<ApiResponse<T>> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /** Raw text response (e.g. CSV export). */
  async getText(endpoint: string, allowRetry = true): Promise<string> {
    const url = `${this.baseUrl}${endpoint}`;
    const headers: Record<string, string> = {
      Accept: 'text/csv, text/plain, */*',
    };
    if (this.memoryToken) {
      headers['Authorization'] = `Bearer ${this.memoryToken}`;
    }

    const response = await fetch(url, {
      method: 'GET',
      headers,
      credentials: 'include',
    });
    this.captureCsrf(response);

    if (response.status === 401 && allowRetry) {
      const refreshed = await this.tryRefresh();
      if (refreshed) return this.getText(endpoint, false);
      this.clearSessionSecrets();
      if (window.location.pathname !== '/admin/login') {
        window.location.href = '/admin/login';
      }
      throw new Error('Unauthorized. Please login again.');
    }

    const text = await response.text();
    if (!response.ok) {
      throw new Error(text.slice(0, 500) || `Request failed with status ${response.status}`);
    }
    return text;
  }
}

export const apiClient = new ApiClient(API_BASE_URL);
export default apiClient;
